import './i18n';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { initErrorReporting } from '@/analytics/errorReporting';
import { applyCachedAccountColor } from '@/lib/colors';
import { handlePreloadError } from '@/lib/preloadError';
import { doNothing } from '@/utils/doNothing';

initErrorReporting();

// After a redeploy the old hashed chunks 404; reload once (cooldown-guarded) to fetch the fresh build.
window.addEventListener('vite:preloadError', handlePreloadError);

const MOCK_SERVICE_WORKER_SCRIPT = '/mockServiceWorker.js';

const unregisterStaleMockWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  const staleMockWorkers = registrations.filter((registration) =>
    registration.active?.scriptURL.endsWith(MOCK_SERVICE_WORKER_SCRIPT),
  );
  await Promise.all(staleMockWorkers.map((registration) => registration.unregister()));
};

const enableMocking = async () => {
  const isViteDevServer = Boolean(import.meta.hot);
  const useMsw = isViteDevServer && import.meta.env.VITE_MOCKING_ENABLED === 'true';
  if (!useMsw) {
    await unregisterStaleMockWorker().catch(doNothing);
    return;
  }

  const { worker } = await import('./mocks/browser');
  return worker.start({
    onUnhandledRequest: 'bypass',
    quiet: false,
  });
};

enableMocking().then(() => {
  applyCachedAccountColor();
  createRoot(document.getElementById('root')!).render(<App />);
});
